<template>
  <div style="display: none;"></div>
</template>

<script>
import {
  collection,
  getDocs,
  doc,
  getDoc,
  getFirestore,
  query,
  where,
  orderBy,
  onSnapshot,
  limit
} from "firebase/firestore";

export default {
  name: "PostNotifications",
  
  data() {
    return {
      userCommunities: [],
      publicCommunitiesUnsubscribes: {},
      publicCommunitiesNewPosts: {},
      communitiesWithNewPosts: []
    };
  },

  async mounted() {
    await this.fetchUserCommunities();
    this.subscribeToAllPublicCommunities();
  },

  computed: {
    isUserAdminOrSU() {
      const role = this.$store.state.Auth.token.claims.type;
      return role && ["admin", "superuser","communityModerator"].includes(role);
    }
  },

  methods: {
    async fetchUserCommunities() {
      const db = getFirestore();
      const userId = this.$store.state.Auth.token.claims.user_id;
      const userDoc = await getDoc(doc(db, "users", userId));
      const userData = userDoc.data();

      let userCommunityIds = userData.enabledCommunities || [];

      // Always include 'general' community
      if (!userCommunityIds.includes("general")) {
        userCommunityIds.unshift("general");
      }

      // Get all communities to check for public ones
      const communitiesSnapshot = await getDocs(collection(db, "communities"));
      
      // Fetch community details
      let communities = [];
      // Add general community first
      communities.push({ id: "general", name: "General", isPublic: true });
      
      // Add all public communities and user's communities
      for (const communityDoc of communitiesSnapshot.docs) {
        const communityData = communityDoc.data();
        // Include if it's a public community or in user's enabled communities
        if (communityData.isPublic || userCommunityIds.includes(communityDoc.id)) {
          communities.push({
            id: communityDoc.id,
            ...communityData
          });
        }
      }

      // Sort communities: first public communities, then private ones
      communities.sort((a, b) => {
        // General community always comes first
        if (a.id === 'general') return -1;
        if (b.id === 'general') return 1;
        
        // Then sort by public status (public ones first)
        if (a.isPublic && !b.isPublic) return -1;
        if (!a.isPublic && b.isPublic) return 1;
        
        // Finally sort alphabetically by name
        return a.name.localeCompare(b.name);
      });

      this.userCommunities = communities;
    },

    subscribeToAllPublicCommunities() {
      // First clean up any existing subscriptions
      Object.values(this.publicCommunitiesUnsubscribes).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
      
      this.publicCommunitiesUnsubscribes = {};
      this.publicCommunitiesNewPosts = {};
      
      // Subscribe to all communities
      this.userCommunities.forEach(community => {
        this.subscribeToCommunityPosts(community.id);
      });
    },
    
    subscribeToCommunityPosts(communityId) {
      const db = getFirestore();
      const postsRef = collection(db, "posts");
      const q = query(
        postsRef,
        where("communityId", "==", communityId),
        orderBy("created.date", "desc"),
        limit(10)
      );
      
      // Initialize counter for this community
      this.publicCommunitiesNewPosts[communityId] = 0;
      
      // Subscribe to updates
      this.publicCommunitiesUnsubscribes[communityId] = onSnapshot(q, (snapshot) => {
        // Check for new posts since last check
        const lastCheck = this.$store.state.lastPostCheck / 1000 || 0;
        
        // Count new posts
        const newPostsCount = snapshot.docs.filter(doc => {
          const post = doc.data();
          return post.created?.date?.seconds > lastCheck;
        }).length;
        
        // Update the count for this community
        this.publicCommunitiesNewPosts[communityId] = newPostsCount;
        
        // Update the list of communities with new posts
        this.updateCommunitiesWithNewPosts();
      });
    },
    
    updateCommunitiesWithNewPosts() {
      // Get communities with new posts with detailed info for the drawer
      const communitiesWithDetail = Object.entries(this.publicCommunitiesNewPosts)
        .filter(([, count]) => count > 0)
        .map(([communityId, count]) => {
          // Find community object
          const community = this.userCommunities.find(c => c.id === communityId);
          return {
            id: communityId,
            name: community ? community.name : communityId,
            count: count
          };
        });
      
      this.communitiesWithNewPosts = communitiesWithDetail;
      
      // Emit communities with new posts for the drawer display
      this.$emit('update-communities', communitiesWithDetail);
      
      // Get names only for the tooltip
      const communitiesNamesOnly = communitiesWithDetail.map(c => c.name);
      
      // Calculate total unread posts across all communities
      const totalUnread = Object.values(this.publicCommunitiesNewPosts)
        .reduce((sum, count) => sum + count, 0);
      
      // Emit total unread count to update the navbar badge
      this.$emit('update-unread', totalUnread);
      
      // Also emit the list of communities with new posts for tooltip
      const communitiesText = communitiesNamesOnly.join(', ');
      this.$emit('update-unread-details', communitiesText);
    },
    
    clearUnreadForCommunity(communityId) {
      // Reset counter for this community
      if (this.publicCommunitiesNewPosts[communityId]) {
        this.publicCommunitiesNewPosts[communityId] = 0;
        
        // Update the communities with new posts list
        this.updateCommunitiesWithNewPosts();
      }
      
      this.$store.dispatch("updateLastPostCheck", Date.now());
    },
    
    /**
     * Public method to clear unread posts for a specific community
     * @param {string} communityId - The ID of the community to clear notifications for
     */
    clearCommunityNotifications(communityId) {
      this.clearUnreadForCommunity(communityId);
    },
    
    /**
     * Public method to clear all unread posts for all communities
     */
    clearAllNotifications() {
      Object.keys(this.publicCommunitiesNewPosts).forEach(communityId => {
        this.publicCommunitiesNewPosts[communityId] = 0;
      });
      this.updateCommunitiesWithNewPosts();
      this.$store.dispatch("updateLastPostCheck", Date.now());
    }
  },

  beforeDestroy() {
    // Clean up all community subscriptions
    Object.values(this.publicCommunitiesUnsubscribes).forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
  }
};
</script> 